/**
 * useCalendar Hook
 *
 * Headless calendar hook providing calendar logic without UI.
 * Separates calendar state management from rendering.
 *
 * Following SOLID principles:
 * - Single Responsibility: Manages calendar state and logic only
 * - Dependency Inversion: Depends on strategy abstraction, not concrete implementation
 *
 * Updated: 2025-11-01 - Added minDate/maxDate constraints for booking calendars
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.strategy - Selection strategy (DateRangeStrategy, SingleDayStrategy, etc.)
 * @param {Function} options.onSelectionChange - Callback when selection changes
 * @param {Function} options.onSelectionComplete - Callback when selection is complete
 * @param {Date} options.initialMonth - Initial month to display
 * @param {Date} options.minDate - Optional minimum date constraint (cannot navigate before)
 * @param {Date} options.maxDate - Optional maximum date constraint (cannot navigate after)
 * @returns {Object} Calendar state and methods
 */

import { useState, useCallback, useMemo } from 'react';

export function useCalendar({
  strategy,
  onSelectionChange,
  onSelectionComplete,
  initialMonth = new Date(),
  minDate = null,
  maxDate = null
}) {
  // Core state
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const [hoveredDate, setHoveredDate] = useState(null);

  /**
   * Handle date selection
   */
  const selectDate = useCallback((date) => {
    const newSelection = strategy.selectDate(date, selectedDates);
    setSelectedDates(newSelection);
    onSelectionChange?.(newSelection);

    // Check if selection is complete
    if (strategy.isSelectionComplete(newSelection)) {
      const validation = strategy.validate(newSelection);
      if (validation.isValid) {
        onSelectionComplete?.(newSelection);
      }
    }
  }, [strategy, selectedDates, onSelectionChange, onSelectionComplete]);

  /**
   * Check if a date is selected
   */
  const isDateSelected = useCallback((date) => {
    return selectedDates.some(selected =>
      selected.toDateString() === date.toDateString()
    );
  }, [selectedDates]);

  /**
   * Check if a date is in the selected range (for range strategies)
   */
  const isDateInRange = useCallback((date) => {
    return strategy.isDateInRange?.(date, selectedDates) || false;
  }, [strategy, selectedDates]);

  /**
   * Check if can navigate to previous month
   */
  const canGoPrevious = useCallback(() => {
    if (!minDate) {return true;}

    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(currentMonth.getMonth() - 1);
    prevMonth.setDate(1);
    prevMonth.setHours(0, 0, 0, 0);

    const minMonthStart = new Date(minDate);
    minMonthStart.setDate(1);
    minMonthStart.setHours(0, 0, 0, 0);

    return prevMonth >= minMonthStart;
  }, [currentMonth, minDate]);

  /**
   * Check if can navigate to next month
   */
  const canGoNext = useCallback(() => {
    if (!maxDate) {return true;}

    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(currentMonth.getMonth() + 1);
    nextMonth.setDate(1);
    nextMonth.setHours(0, 0, 0, 0);

    const maxMonthStart = new Date(maxDate);
    maxMonthStart.setDate(1);
    maxMonthStart.setHours(0, 0, 0, 0);

    return nextMonth <= maxMonthStart;
  }, [currentMonth, maxDate]);

  /**
   * Navigate to previous month
   */
  const navigateToPrevMonth = useCallback(() => {
    if (!canGoPrevious()) {return;}
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() - 1);
      return newMonth;
    });
  }, [canGoPrevious]);

  /**
   * Navigate to next month
   */
  const navigateToNextMonth = useCallback(() => {
    if (!canGoNext()) {return;}
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + 1);
      return newMonth;
    });
  }, [canGoNext]);

  /**
   * Go to a specific month
   */
  const goToMonth = useCallback((month) => {
    setCurrentMonth(month);
  }, []);

  /**
   * Get all days in current month with padding from previous/next months
   */
  const daysInMonth = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];

    // Add padding days from previous month
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push(date);
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    // Add padding days from next month
    const lastDayOfWeek = lastDay.getDay();
    const remainingDays = 6 - lastDayOfWeek;
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  }, [currentMonth]);

  /**
   * Clear selection
   */
  const clearSelection = useCallback(() => {
    setSelectedDates([]);
    strategy.clearSelection?.();
  }, [strategy]);

  /**
   * Validate current selection
   */
  const validateSelection = useCallback(() => {
    return strategy.validate(selectedDates);
  }, [strategy, selectedDates]);

  /**
   * Get selection display string
   */
  const selectionDisplay = useMemo(() => {
    return strategy.getSelectionDisplay(selectedDates);
  }, [strategy, selectedDates]);

  /**
   * Check if a date is available for selection
   */
  const isDateAvailable = useCallback((date) => {
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return false;
    }

    // Check strategy-specific constraints
    const range = strategy.getSelectableRange(selectedDates);
    if (range) {
      if (range.minDate && date < range.minDate) {return false;}
      if (range.maxDate && date > range.maxDate) {return false;}
    }

    return true;
  }, [strategy, selectedDates]);

  /**
   * Check if a date belongs to the current month
   */
  const isDateInCurrentMonth = useCallback((date) => {
    return date.getMonth() === currentMonth.getMonth() &&
           date.getFullYear() === currentMonth.getFullYear();
  }, [currentMonth]);

  return {
    // State
    selectedDates,
    currentMonth,
    hoveredDate,
    daysInMonth,
    selectionDisplay,

    // Actions
    selectDate,
    clearSelection,
    navigateToPrevMonth,
    navigateToNextMonth,
    goToMonth,
    setHoveredDate,

    // Computed
    isDateAvailable,
    isDateSelected,
    isDateInRange,
    isDateInCurrentMonth,
    validateSelection,
    isSelectionComplete: strategy.isSelectionComplete(selectedDates),
    canGoPrevious,
    canGoNext
  };
}

export default useCalendar;
